import { CONVERSATION_PRIORITY_ORDER } from 'shared/constants/messages';

export const findPendingMessageIndex = (chat, message) => {
  const { echo_id: tempMessageId } = message;
  return chat.messages.findIndex(
    m => m.id === message.id || m.id === tempMessageId
  );
};

/**
 * Aceleres 2026-09-09: a mesma mensagem e escrita no store mais do que uma vez —
 * a resposta do POST e, logo a seguir, o broadcast `message.created` do
 * ActionCable. So a primeira passa por `mergeLocalMediaPreviews`; a segunda vem
 * do servidor sem o `local_preview_url` e apagava-o. Quando isso acontecia antes
 * de a imagem remota acabar de descarregar, a bolha voltava ao cinzento — era
 * um dos "balao vazio" intermitentes. O blob so morre quando o anexo deixa de
 * existir.
 */
export const keepLocalMediaPreview = (anterior, proxima) => {
  const anteriores = anterior?.attachments;
  const proximas = proxima?.attachments;
  if (!Array.isArray(anteriores) || !Array.isArray(proximas)) return proxima;

  let mudou = false;
  const attachments = proximas.map((attachment, index) => {
    const blob = anteriores[index]?.local_preview_url;
    if (!blob || attachment?.local_preview_url) return attachment;
    mudou = true;
    return { ...attachment, local_preview_url: blob };
  });

  return mudou ? { ...proxima, attachments } : proxima;
};

export const filterByStatus = (chatStatus, filterStatus) =>
  filterStatus === 'all' ? true : chatStatus === filterStatus;

export const filterByInbox = (shouldFilter, inboxId, chatInboxId) => {
  const isOnInbox = Number(inboxId) === chatInboxId;
  return inboxId ? isOnInbox && shouldFilter : shouldFilter;
};

export const filterByTeam = (shouldFilter, teamId, chatTeamId) => {
  const isOnTeam = Number(teamId) === chatTeamId;
  return teamId ? isOnTeam && shouldFilter : shouldFilter;
};

export const filterByLabel = (shouldFilter, labels, chatLabels) => {
  const isOnLabel = labels.every(label => chatLabels.includes(label));
  return labels.length ? isOnLabel && shouldFilter : shouldFilter;
};
export const filterByUnattended = (
  shouldFilter,
  conversationType,
  firstReplyOn,
  waitingSince
) => {
  return conversationType === 'unattended'
    ? (!firstReplyOn || !!waitingSince) && shouldFilter
    : shouldFilter;
};

export const applyPageFilters = (conversation, filters) => {
  const { inboxId, status, labels = [], teamId, conversationType } = filters;
  const {
    status: chatStatus,
    inbox_id: chatInboxId,
    labels: conversationLabels = [],
    contact_labels: contactLabels = [],
    meta = {},
    first_reply_created_at: firstReplyOn,
    waiting_since: waitingSince,
  } = conversation;
  const chatLabels = [
    ...(Array.isArray(conversationLabels) ? conversationLabels : []),
    ...(Array.isArray(contactLabels) ? contactLabels : []),
  ];
  const team = meta.team || {};
  const { id: chatTeamId } = team;

  let shouldFilter = filterByStatus(chatStatus, status);
  shouldFilter = filterByInbox(shouldFilter, inboxId, chatInboxId);
  shouldFilter = filterByTeam(shouldFilter, teamId, chatTeamId);
  shouldFilter = filterByLabel(shouldFilter, labels, chatLabels);
  shouldFilter = filterByUnattended(
    shouldFilter,
    conversationType,
    firstReplyOn,
    waitingSince
  );

  return shouldFilter;
};

/**
 * Filters conversations based on user role and permissions
 *
 * @param {Object} conversation - The conversation object to check permissions for
 * @param {string} role - The user's role (administrator, agent, etc.)
 * @param {Array<string>} permissions - List of permission strings the user has
 * @param {number|string} currentUserId - The ID of the current user
 * @returns {boolean} - Whether the user has permissions to access this conversation
 */
export const applyRoleFilter = (
  conversation,
  role,
  permissions,
  currentUserId
) => {
  // the role === "agent" check is typically not correct on it's own
  // the backend handles this by checking the custom_role_id at the user model
  // here however, the `getUserRole` returns "custom_role" if the id is present,
  // so we can check the role === "agent" directly
  if (['administrator', 'agent'].includes(role)) {
    return true;
  }

  // Check for full conversation management permission
  if (permissions.includes('conversation_manage')) {
    return true;
  }

  const conversationAssignee = conversation.meta.assignee;
  const isUnassigned = !conversationAssignee;
  const isAssignedToUser = conversationAssignee?.id === currentUserId;

  // Check unassigned management permission
  if (permissions.includes('conversation_unassigned_manage')) {
    return isUnassigned || isAssignedToUser;
  }

  // Check participating conversation management permission
  if (permissions.includes('conversation_participating_manage')) {
    return isAssignedToUser;
  }

  return false;
};

const SORT_OPTIONS = {
  last_activity_at_asc: ['sortOnLastActivityAt', 'asc'],
  last_activity_at_desc: ['sortOnLastActivityAt', 'desc'],
  created_at_asc: ['sortOnCreatedAt', 'asc'],
  created_at_desc: ['sortOnCreatedAt', 'desc'],
  priority_asc: ['sortOnPriority', 'asc'],
  priority_desc: ['sortOnPriority', 'desc'],
  waiting_since_asc: ['sortOnWaitingSince', 'asc'],
  waiting_since_desc: ['sortOnWaitingSince', 'desc'],
  priority_desc_created_at_asc: ['sortOnPriorityCreatedAt', 'desc'],
  unread_first: ['sortOnUnreadFirst', 'desc'],
};
const normalizeSortValue = value => {
  const numericValue = Number(value);
  if (Number.isFinite(numericValue)) return numericValue;

  if (typeof value === 'string' && value.trim()) {
    const parsedDate = Date.parse(value);
    if (Number.isFinite(parsedDate)) return parsedDate;
  }

  return 0;
};

const sortAscending = (valueA, valueB) =>
  normalizeSortValue(valueA) - normalizeSortValue(valueB);
const sortDescending = (valueA, valueB) =>
  normalizeSortValue(valueB) - normalizeSortValue(valueA);

const getSortOrderFunction = sortOrder =>
  sortOrder === 'asc' ? sortAscending : sortDescending;

const sortWithNullsLast = (valueA, valueB, sortDirection) => {
  const missingA = valueA === null || valueA === undefined || valueA === '';
  const missingB = valueB === null || valueB === undefined || valueB === '';

  if (missingA || missingB) {
    if (missingA && missingB) return 0;
    return missingA ? 1 : -1;
  }

  return getSortOrderFunction(sortDirection)(valueA, valueB);
};

const sortConfig = {
  sortOnLastActivityAt: (a, b, sortDirection) =>
    sortWithNullsLast(a.last_activity_at, b.last_activity_at, sortDirection),

  sortOnCreatedAt: (a, b, sortDirection) =>
    sortWithNullsLast(a.created_at, b.created_at, sortDirection),

  sortOnPriority: (a, b, sortDirection) => {
    const DEFAULT_FOR_NULL = sortDirection === 'asc' ? 5 : 0;

    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;

    const priorityResult = getSortOrderFunction(sortDirection)(p1, p2);
    return (
      priorityResult || sortDescending(a.last_activity_at, b.last_activity_at)
    );
  },

  sortOnPriorityCreatedAt: (a, b) => {
    const DEFAULT_FOR_NULL = 0;
    const p1 = CONVERSATION_PRIORITY_ORDER[a.priority] || DEFAULT_FOR_NULL;
    const p2 = CONVERSATION_PRIORITY_ORDER[b.priority] || DEFAULT_FOR_NULL;
    if (p1 !== p2) return p2 - p1;
    return sortAscending(a.created_at, b.created_at);
  },

  sortOnWaitingSince: (a, b, sortDirection) => {
    const sortFunc = getSortOrderFunction(sortDirection);
    const hasWaitingSince = value =>
      value !== null &&
      value !== undefined &&
      value !== '' &&
      normalizeSortValue(value) !== 0;
    const hasWaitingSinceA = hasWaitingSince(a.waiting_since);
    const hasWaitingSinceB = hasWaitingSince(b.waiting_since);

    if (!hasWaitingSinceA || !hasWaitingSinceB) {
      if (!hasWaitingSinceA && !hasWaitingSinceB) {
        return sortAscending(a.created_at, b.created_at);
      }

      // O backend mantém valores nulos no fim, independentemente da direção.
      return hasWaitingSinceA ? -1 : 1;
    }

    return (
      sortFunc(a.waiting_since, b.waiting_since) ||
      sortAscending(a.created_at, b.created_at)
    );
  },
  sortOnUnreadFirst: (a, b) => {
    const aUnread = (a.unread_count || 0) > 0 ? 1 : 0;
    const bUnread = (b.unread_count || 0) > 0 ? 1 : 0;
    if (aUnread !== bUnread) return bUnread - aUnread;
    return sortDescending(a.last_activity_at, b.last_activity_at);
  },
};

export const sortComparator = (a, b, sortKey) => {
  const [sortMethod, sortDirection] =
    SORT_OPTIONS[sortKey] || SORT_OPTIONS.last_activity_at_desc;
  const result = sortConfig[sortMethod](a, b, sortDirection);
  if (Number.isFinite(result) && result !== 0) {
    return result;
  }

  const ascendingIdSorts = [
    'last_activity_at_asc',
    'created_at_asc',
    'waiting_since_asc',
    'waiting_since_desc',
    'priority_desc_created_at_asc',
  ];
  const idSortDirection = ascendingIdSorts.includes(sortKey) ? 'asc' : 'desc';

  // Timestamps chegam ao frontend com precisão de segundos. O ID elimina
  // empates sem alterar a direção escolhida pelo usuário.
  return getSortOrderFunction(idSortDirection)(
    Number(a.id || 0),
    Number(b.id || 0)
  );
};

export const sortComparatorWithPinned = (a, b, sortKey) => {
  const pinDifference =
    Number(Boolean(b?.is_pinned)) - Number(Boolean(a?.is_pinned));
  if (pinDifference) return pinDifference;

  if (a?.is_pinned && b?.is_pinned) {
    const aPinnedAt = Date.parse(a.pinned_at || '');
    const bPinnedAt = Date.parse(b.pinned_at || '');
    if (Number.isFinite(aPinnedAt) && Number.isFinite(bPinnedAt)) {
      const pinnedAtDifference = aPinnedAt - bPinnedAt;
      if (pinnedAtDifference) return pinnedAtDifference;
    }
  }

  return sortComparator(a, b, sortKey);
};
